/**
 * Cantidad de un renglón de CUADRILLA — una sola interpretación para los tres lados.
 *
 * Hermana de `apuEquipo.ts`, y por el mismo motivo: que el número que se MUESTRA sea el
 * que se COBRA. Aquel existe porque una grúa torre aparecía como "6 horas × 124" con un
 * importe de 93, y 6 × 124 no es 93. Este existe para que no vuelva a pasar con el
 * operador de una máquina.
 *
 * EL CASO. Un componente normal de la cuadrilla dice cuánta gente de ese oficio hay, y su
 * costo es `Σ(cantidad × salario) ÷ rendimiento`: el albañil está todo el día produciendo,
 * así que su jornada se reparte entre lo que se produce ese día. El OPERADOR de una máquina
 * no: trabaja las horas que trabaja la máquina. Una grúa usada 0,1 h por m² no ocupa a su
 * operador una jornada entera por cada m². Por eso su componente trae
 * `jornadas_por_unidad` y el worker lo costea aparte, sin dividir por el rendimiento.
 *
 * PERO EL RESTO DEL MUNDO NO CONOCE ESE CAMPO. La vista del análisis muestra la columna
 * "cantidad" y m1 toma `cantidad`, suma `Σ(cantidad × precio)` y divide por el
 * rendimiento. Si a los dos les llega un `1`, la pantalla miente y m1 cobra de más: en el
 * caso real de producción, 222,58 por m² en vez de 15,90.
 *
 * LA CONVERSIÓN. `cantidad equivalente = jornadas_por_unidad × rendimiento`. Con eso, la
 * cuenta de siempre —dividir por el rendimiento— aterriza exactamente en
 * `jornadas_por_unidad × salario`, que es lo que el worker cobra. La columna vuelve a
 * cerrar y m1 recibe lo mismo que muestra m0.
 *
 * Sin rendimiento utilizable no se convierte: se devuelve la cantidad tal cual, que es el
 * comportamiento de siempre. De un concepto sin rendimiento no sale un costo de todos
 * modos, y dividir por un número inventado sería peor.
 */

export interface CuadrillaCantidadLike {
  /** Cuánta gente de ese oficio hay en la cuadrilla. La forma normal. */
  cantidad?: number | null;
  /**
   * Jornadas por UNIDAD del concepto. Solo la trae el operador que el sistema agrega
   * por una máquina: su tiempo lo manda la máquina, no el rendimiento de la cuadrilla.
   */
  jornadas_por_unidad?: number | null;
}

/**
 * La cantidad que hay que mostrar y exportar, en la base que todos entienden: la que
 * multiplicada por el salario y dividida por el rendimiento da el costo real.
 */
export function cantidadCuadrillaPorUnidad(
  comp: CuadrillaCantidadLike | null | undefined,
  rendimiento: number | null | undefined,
): number {
  const jpu = Number(comp?.jornadas_por_unidad);
  const rend = Number(rendimiento);
  if (Number.isFinite(jpu) && jpu > 0 && Number.isFinite(rend) && rend > 0) {
    return jpu * rend;
  }
  const cant = Number(comp?.cantidad);
  return Number.isFinite(cant) ? cant : 1;
}
